import { LOGIN_NO_ORG_ERROR_CODE } from "@/lib/auth/loginErrorCodes"
import { isSafeRedirectPath } from "@/lib/utils/isSafeRedirectPath"

export interface ResolveLandingDestinationArgs {
  /**
   * Optional post-login redirect path. Callers should sanitize this to a
   * same-origin single-slash path before passing it in. This function also
   * validates it as defense-in-depth — external URLs and scheme-relative paths
   * (`//evil.com`) are ignored.
   */
  redirectTo?: string | null
  /** Value of the `siro_last_visited_organization_id` cookie, if any. */
  lastVisitedOrgId?: string | null
  /** The user's home org from the JWT (`user.organizationId`). */
  defaultOrgId: string | null | undefined
  /**
   * Path (and query string) inside the org scope to land on, e.g.
   * `"recordings/123?t=500"`. Omitted → the org root
   * (`/organization/{id}`), whose page picks the viewer's home surface:
   * the Coaching Assistant for a coach whose org has it, Ask Siro otherwise
   * (`app/organization/[organizationId]/page.tsx`). Keeping that decision
   * in one route means every entry point — `/`, the login forms, the
   * org-less catch-all — lands the same way without each reading the
   * viewer's profile.
   *
   * Interpolated as-is, so it must arrive **already encoded**. Encoding can't
   * happen here: this is one pre-built string carrying its own `/` and `?`
   * separators, so encoding it wholesale would destroy them. `proxy.ts` feeds
   * it via `resolveOrgScopedRedirect`, which forwards `nextUrl.pathname`
   * without ever decoding it.
   */
  path?: string
}

/**
 * The single source of truth for "where should this user land?". Used by
 * `/` (Server Component redirect), the login form (post-success
 * navigation), and the org-less catch-all route. Hierarchy:
 *
 *   1. `?redirect=` (login flow only) — explicit user / proxy intent.
 *   2. `lastVisitedOrgId` — drop the user back where they were.
 *   3. `defaultOrgId` — the user's home org from the JWT.
 *
 * `"/"` for `redirectTo` is treated as no redirect (callers default to `"/"`
 * when the URL has no `?redirect=` param). Returns a `/login?error=…` path
 * when no valid org ID is available.
 */
export const resolveLandingDestination = ({
  redirectTo,
  lastVisitedOrgId,
  defaultOrgId,
  path,
}: ResolveLandingDestinationArgs): string => {
  // Exclude `/login*` targets: a `/login/workos/redeem?code=…` redirect would
  // re-redeem the code after login and swap the account.
  if (
    redirectTo &&
    redirectTo !== "/" &&
    isSafeRedirectPath(redirectTo) &&
    !redirectTo.toLowerCase().startsWith("/login")
  )
    return redirectTo
  const orgId = lastVisitedOrgId ?? defaultOrgId
  if (!orgId) return `/login?error=${LOGIN_NO_ORG_ERROR_CODE}`
  return path ? `/organization/${orgId}/${path}` : `/organization/${orgId}`
}
